import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../../api/client';
import type { EventSlots, RunSummary } from '../../api/types';
import { Banner, Card, Empty, formatDate, secondaryLabel } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';
import EventSlotsEditor from './EventSlotsEditor';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function inThreeMonths(): string {
  const date = new Date();
  date.setMonth(date.getMonth() + 3);
  return date.toISOString().slice(0, 10);
}

const PLAN_STATUS: Record<string, string> = {
  DRAFT: 'brouillon',
  PUBLISHED: 'publié',
  ARCHIVED: 'archivé',
};

function runLabel(run: RunSummary): string {
  const created = new Date(run.created_at).toLocaleDateString('fr-FR');
  const name = run.label ?? `généré le ${created}`;
  return `${name} — ${PLAN_STATUS[run.plan_status] ?? run.plan_status}`;
}

/**
 * Affectations d'une période, modifiables.
 *
 * L'écran de génération montre une proposition ; celui-ci sert à vivre avec
 * elle. Entre le moment où un planning est publié et le dimanche concerné, il
 * se passe des choses : quelqu'un tombe malade, quelqu'un se propose. Corriger
 * doit alors coûter trois clics, pas une régénération complète — c'est
 * pourquoi un planning publié reste modifiable ici, et qu'une correction est
 * figée automatiquement pour qu'une régénération ultérieure la respecte.
 *
 * Les exports sont au même endroit, parce qu'ils répondent au même moment du
 * travail : on corrige, puis on imprime ou on envoie.
 */
export default function AssignmentsScreen() {
  const runs = useAsync<RunSummary[]>(() => api.runs(), []);
  const [runId, setRunId] = useState<number | null>(null);
  const [slots, setSlots] = useState<EventSlots[] | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(inThreeMonths());

  // Par défaut, le planning sur lequel il y a quelque chose à faire : le
  // planning en cours, ou le brouillon le plus récent.
  const defaultRun = useMemo(() => {
    const list = runs.data ?? [];
    return (
      list.find((run) => run.plan_status === 'PUBLISHED') ??
      list.find((run) => run.plan_status === 'DRAFT') ??
      list[0] ??
      null
    );
  }, [runs.data]);

  useEffect(() => {
    if (runId === null && defaultRun !== null) setRunId(defaultRun.id);
  }, [defaultRun, runId]);

  const selected = useMemo(
    () => (runs.data ?? []).find((run) => run.id === runId) ?? null,
    [runs.data, runId],
  );

  // La période des exports suit le planning choisi : c'est presque toujours
  // celle qu'on veut imprimer, et la retaper à chaque fois serait une corvée.
  useEffect(() => {
    if (selected === null) return;
    setFrom(selected.horizon_start);
    setTo(selected.horizon_end);
  }, [selected]);

  useEffect(() => {
    if (runId === null) return;
    let active = true;
    setLoadingSlots(true);
    setError(null);
    api
      .periodSlots(runId)
      .then((value) => {
        if (active) setSlots(value);
      })
      .catch((cause) => {
        if (!active) return;
        setSlots(null);
        setError(cause instanceof ApiError ? cause.detail : 'Chargement impossible.');
      })
      .finally(() => {
        if (active) setLoadingSlots(false);
      });
    return () => {
      active = false;
    };
  }, [runId]);

  const guard = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.detail : 'Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  const change = (eventId: number) => (roleId: number, person: number | null, previous: number | null) =>
    guard(async () => {
      if (runId === null) return;
      const updated = await api.updateSlot(runId, eventId, roleId, {
        person_id: person,
        previous_person_id: previous,
        lock: true,
      });
      // Le serveur renvoie l'événement entier : changer un poste change qui
      // est affectable aux autres, et la liste doit le refléter aussitôt.
      setSlots((current) =>
        (current ?? []).map((item) => (item.event_id === eventId ? updated : item)),
      );
      setNotice(
        person === null
          ? 'Poste libéré. Le choix est enregistré : une régénération ne le remplira pas d’office.'
          : 'Affectation enregistrée et figée : une régénération la conservera.',
      );
    });

  const download = (format: 'xlsx' | 'pdf') =>
    guard(async () => {
      if (format === 'xlsx') await api.exportXlsx(from, to);
      else await api.exportPdf(from, to);
      setNotice('Export téléchargé.');
    });

  const reprendre = () =>
    guard(async () => {
      if (runId === null) return;
      const draft = await api.duplicateAsDraft(runId);
      runs.reload();
      setRunId(draft.id);
      setNotice('Planning repris en brouillon : il est de nouveau modifiable.');
    });

  return (
    <div className="stack">
      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="success">{notice}</Banner>}

      <Card title="Planning à administrer">
        {runs.loading ? (
          <p className="muted">Chargement…</p>
        ) : (runs.data ?? []).length === 0 ? (
          <Empty>
            Aucun planning n’a encore été généré. Commencez par l’écran « Générer ».
          </Empty>
        ) : (
          <div className="row">
            <div style={{ flex: '1 1 260px' }}>
              <label htmlFor="run">Planning</label>
              <select
                id="run"
                value={runId === null ? '' : String(runId)}
                onChange={(event) => {
                  setRunId(Number(event.target.value));
                  setSlots(null);
                }}
                style={{ width: '100%' }}
              >
                {(runs.data ?? []).map((run) => (
                  <option key={run.id} value={String(run.id)}>
                    {runLabel(run)}
                  </option>
                ))}
              </select>
            </div>
            {selected !== null && (
              <p className="muted" style={{ flex: '1 1 240px', margin: 0 }}>
                Du {new Date(selected.horizon_start).toLocaleDateString('fr-FR')} au{' '}
                {new Date(selected.horizon_end).toLocaleDateString('fr-FR')}.
                {selected.plan_status === 'PUBLISHED' &&
                  ' C’est le planning en cours : une correction prend effet immédiatement.'}
                {selected.plan_status === 'ARCHIVED' &&
                  ' Un planning archivé est une trace : il ne se modifie plus.'}
              </p>
            )}
            {selected?.plan_status === 'ARCHIVED' && (
              <button onClick={reprendre} disabled={busy}>
                Reprendre en brouillon
              </button>
            )}
          </div>
        )}
      </Card>

      <Card title="Exporter">
        <div className="row">
          <div>
            <label htmlFor="export-from">Du</label>
            <input
              id="export-from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="export-to">Au</label>
            <input
              id="export-to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <button onClick={() => download('pdf')} disabled={busy}>
            PDF à afficher
          </button>
          <button onClick={() => download('xlsx')} disabled={busy}>
            Tableur Excel
          </button>
        </div>
        <p className="muted">
          L’export reprend le <strong>planning publié</strong> de la période, quelle que
          soit la génération choisie ci-dessus : c’est celui qui est affiché au local et
          que les équipes ont reçu.
        </p>
      </Card>

      {loadingSlots && <p className="muted">Chargement des affectations…</p>}

      {slots !== null && slots.length === 0 && (
        <Empty>Ce planning ne couvre aucune date.</Empty>
      )}

      {(slots ?? []).map((event) => {
        const secondary = secondaryLabel(event.event_label, event.starts_at);
        // Un poste obligatoire vide est la seule chose qui demande une action
        // tout de suite : il doit se voir sans parcourir la date entière.
        const toFill = (event.roles ?? []).reduce(
          (total, role) => total + Math.max(0, role.minimum - (role.assigned ?? []).length),
          0,
        );
        return (
          <Card
            key={event.event_id}
            title={formatDate(event.starts_at)}
            actions={
              runId !== null && (
                <Link className="ghost small" to={`/affectations/${runId}/${event.event_id}`}>
                  Ouvrir cette date
                </Link>
              )
            }
          >
            <div className="spread">
              {secondary ? <p className="muted">{secondary}</p> : <span />}
              {toFill > 0 && (
                <span className="to-fill">
                  {toFill === 1
                    ? '1 poste obligatoire à pourvoir'
                    : `${toFill} postes obligatoires à pourvoir`}
                </span>
              )}
            </div>
            {!event.editable && (
              <Banner kind="info">
                Ce planning n’est plus modifiable. Reprenez-le en brouillon pour y
                travailler.
              </Banner>
            )}
            <EventSlotsEditor slots={event} busy={busy} onChange={change(event.event_id)} />
          </Card>
        );
      })}
    </div>
  );
}
